<script lang="ts" setup>
import { computed } from 'vue'

const lastMetWith = defineModel({ required: true, type: Number })
const deliveryRule = defineModel('deliveryRule', { required: true, type: String, default: 'always' })

const lastMetWithDays = computed({
  get() {
    return lastMetWith.value !== -1 && lastMetWith.value !== 0
  },
  set(val: boolean) {
    if (val) {
      lastMetWith.value = 7 // Default to 7 days when selecting this option
    } else {
      lastMetWith.value = -1 // Default to "not met before" when deselecting
    }
    return true
  }
})
</script>

<template>
  <div class="q-mb-lg">
    <h6 class="text-h6">Meeting Intelligence Rules</h6>
    <p class="text-caption q-mb-md">
      Configure when MeetingHero should generate or refresh meeting intelligence for your contacts.
    </p>

    <q-list>
      <q-item dense tag="label" v-ripple>
        <q-item-section side>
          <q-radio v-model="lastMetWith" :val="-1" />
        </q-item-section>

        <q-item-section>
          <q-item-label>First-time meetings only</q-item-label>
          <q-item-label caption>Generate intelligence only for new contacts you haven't met before.</q-item-label>
        </q-item-section>
      </q-item>

      <q-item dense tag="label" v-ripple>
        <q-item-section side>
          <q-radio v-model="lastMetWithDays" :val="true" />
        </q-item-section>

        <q-item-section>
          <q-item-label>
            Refresh intelligence after
            <q-input
              v-if="lastMetWith !== -1 && lastMetWith !== 0"
              dense
              v-model.number="lastMetWith"
              filled
              type="number"
              style="max-width: 80px; display: inline-block"
            />
            <span v-else> x </span>
            days
          </q-item-label>
          <q-item-label caption
            >Generate fresh intelligence if the last update was more than the specified number of days
            ago.</q-item-label
          >
        </q-item-section>
      </q-item>

      <q-item dense tag="label" v-ripple>
        <q-item-section side>
          <q-radio v-model="lastMetWith" :val="0" />
        </q-item-section>

        <q-item-section>
          <q-item-label>Every meeting</q-item-label>
          <q-item-label caption
            >Generate fresh intelligence before every scheduled meeting, regardless of when it was last
            updated.</q-item-label
          >
        </q-item-section>
      </q-item>
    </q-list>
  </div>

  <div>
    <h6 class="text-h6">Intelligence Delivery Settings</h6>
    <p class="text-caption q-mb-md">Choose when you want to receive meeting intelligence updates.</p>

    <q-list>
      <q-item dense tag="label" v-ripple>
        <q-item-section side>
          <q-radio v-model="deliveryRule" val="always" />
        </q-item-section>

        <q-item-section>
          <q-item-label>Before every meeting</q-item-label>
          <q-item-label caption
            >Receive the most recent intelligence before each meeting, even if it wasn't newly generated for this
            specific contact.</q-item-label
          >
        </q-item-section>
      </q-item>

      <q-item dense tag="label" v-ripple>
        <q-item-section side>
          <q-radio v-model="deliveryRule" val="lastMetWith" />
        </q-item-section>

        <q-item-section>
          <q-item-label>Only when newly generated</q-item-label>
          <q-item-label caption
            >Receive intelligence only when it's freshly generated based on the rules above for the specific meeting
            attendee.</q-item-label
          >
        </q-item-section>
      </q-item>
    </q-list>
  </div>
</template>
